<script setup lang="ts">
import { MessageSquareText, Star } from "lucide-vue-next";
import type { PublicWorkerServiceOption } from "~/types/public-worker";
import { toTitleCase } from "~/utils/search-catalog";

const props = defineProps<{
  modelValue: boolean;
  workerId: number;
  workerName: string;
  services: PublicWorkerServiceOption[];
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  submitted: [];
}>();

const { t } = useI18n();
const toast = useToast();
const rating = ref(0);
const hoveredRating = ref(0);
const serviceId = ref("");
const comment = ref("");
const errorMessage = ref("");
const submitting = ref(false);

function serviceLabel(value: string) {
  const key = `services.${value}`;
  const translated = t(key);

  return translated === key ? toTitleCase(value) : translated;
}

const serviceOptions = computed(() => [
  {
    value: "",
    label: t("worker_profile.review_general_service"),
  },
  ...props.services.map(service => ({
    value: String(service.id),
    label: serviceLabel(service.name),
  })),
]);

function reset() {
  rating.value = 0;
  hoveredRating.value = 0;
  serviceId.value = "";
  comment.value = "";
  errorMessage.value = "";
}

async function submit() {
  errorMessage.value = "";

  if (!rating.value) {
    errorMessage.value = t("worker_profile.review_rating_required");
    return;
  }

  try {
    submitting.value = true;

    await $fetch(`/api/internal/workers/${props.workerId}/reviews`, {
      method: "POST",
      credentials: "include",
      body: {
        rating: rating.value,
        workerServiceId: serviceId.value ? Number(serviceId.value) : null,
        comment: comment.value.trim() || null,
      },
    });

    toast.success({
      title: t("worker_profile.review_submitted_title"),
      description: t("worker_profile.review_submitted_description"),
    });

    emit("submitted");
    emit("update:modelValue", false);
  } catch (error: any) {
    if (error?.response?.status === 401 || error?.statusCode === 401) {
      errorMessage.value = t("worker_profile.review_login_required");
    } else if (error?.response?.status === 409 || error?.statusCode === 409) {
      errorMessage.value = t("worker_profile.review_already_exists");
    } else {
      errorMessage.value =
        error?.data?.statusMessage ||
        error?.statusMessage ||
        t("worker_profile.review_submit_error");
    }
  } finally {
    submitting.value = false;
  }
}

watch(
  () => props.modelValue,
  open => {
    if (open) reset();
  },
  { immediate: true },
);
</script>

<template>
  <WorkerProfileFormModal
    :model-value="modelValue"
    :title="$t('worker_profile.review_modal_title')"
    :description="
      $t('worker_profile.review_modal_description', { name: workerName })
    "
    :confirm-text="$t('worker_profile.submit_review')"
    :cancel-text="$t('worker_profile.cancel')"
    :loading="submitting"
    width="md"
    @update:model-value="emit('update:modelValue', $event)"
    @confirm="submit"
  >
    <template #icon>
      <div
        class="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary"
      >
        <MessageSquareText class="size-5" />
      </div>
    </template>

    <div>
      <label class="text-base font-semibold text-app-text">
        {{ $t("worker_profile.your_rating") }}
      </label>
      <div class="mt-2 flex gap-1">
        <button
          v-for="star in 5"
          :key="star"
          type="button"
          class="rounded p-1 transition hover:scale-110"
          :aria-label="$t('worker_profile.rate_stars', { count: star })"
          @mouseenter="hoveredRating = star"
          @mouseleave="hoveredRating = 0"
          @click="rating = star"
        >
          <Star
            class="size-8"
            :class="
              star <= (hoveredRating || rating)
                ? 'fill-amber-400 text-amber-400'
                : 'text-app-border'
            "
          />
        </button>
      </div>
    </div>

    <div class="mt-5">
      <label class="text-base font-semibold text-app-text">
        {{ $t("worker_profile.review_service") }}
      </label>
      <AppSelect
        v-model="serviceId"
        :options="serviceOptions"
        width="full"
        :sheet-title="$t('worker_profile.review_service')"
        trigger-class="mt-2 flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-app-border bg-white px-3.5 text-left text-base font-medium text-app-text transition hover:border-primary/40"
      />
    </div>

    <div class="mt-5">
      <div class="flex items-center justify-between gap-3">
        <label
          for="worker-review-comment"
          class="text-base font-semibold text-app-text"
        >
          {{ $t("worker_profile.review_comment") }}
        </label>
        <span class="text-sm text-app-muted">{{ comment.length }}/2000</span>
      </div>
      <textarea
        id="worker-review-comment"
        v-model="comment"
        maxlength="2000"
        rows="6"
        class="mt-2 w-full resize-none rounded-lg border border-app-border px-3.5 py-3 text-base leading-6 text-app-text outline-none transition placeholder:text-app-placeholder focus:border-primary focus:ring-2 focus:ring-primary/10"
        :placeholder="$t('worker_profile.review_comment_placeholder')"
      />
    </div>

    <p
      v-if="errorMessage"
      class="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-app-danger"
    >
      {{ errorMessage }}
    </p>
  </WorkerProfileFormModal>
</template>
